import FJImageExtenderEdit from '../../FJImageExtender/FJImageExtenderRight/view/FJImageExtenderEdit';
import FJImageExtenderPreviewCanvas from '../../FJImageExtender/FJImageExtenderRight/view/FJImageExtenderPreviewCanvas';
import styles from './FJMobileImageExtenderCreation.module.less';
import { useRef, useCallback, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import FJConstant from '../../../constants/FJConstant';
import FJMobileCreationViewContainer from '../../FJMainPreview/FJMobileCreationView/FJMobileCreationViewContainer';

const FJMobileImageExtenderCreation = props => {
    const { uploadImageInfo, boardSize, imageRect, setImageRect, taskStatus } = props;
    const previewBoxRef = useRef(null);
    const [previewSize, setPreviewSize] = useState({ width: 0, height: 0 });
    const touchStateRef = useRef({
        isZooming: false,
        startDistance: 0,
        startImageRect: null,
    });

    const boardRect = useMemo(() => {
        const boardRatio = boardSize.width / boardSize.height;
        const borderRect = { width: 0, height: 0, x: 0, y: 0 };

        if (boardRatio > 1) {
            borderRect.width = Math.min(previewSize.width, previewSize.height * boardRatio);
            borderRect.height = borderRect.width / boardRatio;
            borderRect.x = (previewSize.width - borderRect.width) / 2;
            borderRect.y = 0;
        } else {
            borderRect.height = Math.min(previewSize.height, previewSize.width / boardRatio);
            borderRect.width = borderRect.height * boardRatio;
            borderRect.x = (previewSize.width - borderRect.width) / 2;
            borderRect.y = 0;
        }
        return borderRect;
    }, [boardSize, previewSize]);
    const stateImageRect = useMemo(() => {
        const imageRatio = imageRect.width / imageRect.height;
        const curImageRect = { width: 0, height: 0, x: 0, y: 0 };

        if (imageRatio > 1) {
            curImageRect.width = boardRect.width * (imageRect.width / boardSize.width);
            curImageRect.height = curImageRect.width / imageRatio;
            curImageRect.x = boardRect.width * (imageRect.x / boardSize.width) + boardRect.x;
            curImageRect.y = boardRect.height * (imageRect.y / boardSize.height) + boardRect.y;
        } else {
            curImageRect.height = boardRect.height * (imageRect.height / boardSize.height);
            curImageRect.width = curImageRect.height * imageRatio;
            curImageRect.x = boardRect.width * (imageRect.x / boardSize.width) + boardRect.x;
            curImageRect.y = boardRect.height * (imageRect.y / boardSize.height) + boardRect.y;
        }

        return curImageRect;
    }, [imageRect, boardRect, boardSize]);
    const changeImageRect = useCallback(
        (rect, direction) => {
            const newRect = {
                width: (rect.width / boardRect.width) * boardSize.width,
                height: (rect.height / boardRect.height) * boardSize.height,
                x: ((rect.x - boardRect.x) / boardRect.width) * boardSize.width,
                y: ((rect.y - boardRect.y) / boardRect.height) * boardSize.height,
            };
            setImageRect(newRect, direction);
        },
        [setImageRect, boardRect, boardSize],
    );
    useEffect(() => {
        const previewBox = previewBoxRef.current;
        setPreviewSize({ width: previewBox.clientWidth, height: previewBox.clientHeight });
    }, []);
    //#region 移动端双指缩放
    const _getDistance = (touch1, touch2) => {
        const dx = touch1.clientX - touch2.clientX;
        const dy = touch1.clientY - touch2.clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };
    const _onTouchStart = e => {
        e.stopPropagation();
        e.preventDefault();
        if (e.touches.length === 2) {
            const distance = _getDistance(e.touches[0], e.touches[1]);
            touchStateRef.current = {
                isZooming: true,
                startDistance: distance,
                startImageRect: { ...stateImageRect },
            };
        }
    };
    const _onTouchMove = e => {
        if (!touchStateRef.current.isZooming || e.touches.length !== 2) return;

        const currentDistance = _getDistance(e.touches[0], e.touches[1]);
        const calcScale = currentDistance / touchStateRef.current.startDistance;
        const maxScale = Math.min(
            boardRect.width / touchStateRef.current.startImageRect.width,
            boardRect.height / touchStateRef.current.startImageRect.height,
        );
        const minScale = Math.min(
            (boardRect.width * 0.3) / touchStateRef.current.startImageRect.width,
            (boardRect.height * 0.3) / touchStateRef.current.startImageRect.height,
        );
        const useScale = Math.min(maxScale, Math.max(minScale, calcScale));
        const newWidth = touchStateRef.current.startImageRect.width * useScale;
        const newHeight = touchStateRef.current.startImageRect.height * useScale;

        let calcX =
            touchStateRef.current.startImageRect.x - (newWidth - touchStateRef.current.startImageRect.width) / 2;
        let calcY =
            touchStateRef.current.startImageRect.y - (newHeight - touchStateRef.current.startImageRect.height) / 2;
        if (calcX < boardRect.x) {
            calcX = boardRect.x;
        } else if (calcX + newWidth > boardRect.x + boardRect.width) {
            calcX = boardRect.x + boardRect.width - newWidth;
        }
        if (calcY < boardRect.y) {
            calcY = boardRect.y;
        } else if (calcY + newHeight >= boardRect.y + boardRect.height) {
            calcY = boardRect.y + boardRect.height - newHeight;
        }
        const newRect = {
            width: newWidth,
            height: newHeight,
            x: calcX,
            y: calcY,
        };

        changeImageRect(newRect, 'scale');
    };
    const _onTouchEnd = () => {
        touchStateRef.current = {
            isZooming: false,
            startDistance: 0,
            startImageRect: null,
        };
    };
    //#endregion
    const isShowCreationLoading = taskStatus !== FJConstant.DEFAULT;
    return (
        <div className={styles.container} style={{ padding: isShowCreationLoading ? '0' : '0 16px 16px 16px' }}>
            <div
                className={styles.preview_box}
                ref={previewBoxRef}
                onTouchStart={_onTouchStart}
                onTouchMove={_onTouchMove}
                onTouchEnd={_onTouchEnd}
                style={{ display: isShowCreationLoading ? 'none' : 'block' }}
            >
                <FJImageExtenderPreviewCanvas
                    imageRect={stateImageRect}
                    boardRect={boardRect}
                    previewSize={previewSize}
                    uploadImageInfo={uploadImageInfo}
                />
                <FJImageExtenderEdit
                    imageRect={stateImageRect}
                    boardRect={boardRect}
                    changeImageRect={changeImageRect}
                />
            </div>
            {isShowCreationLoading && (
                <FJMobileCreationViewContainer forbiddenPreview={true} style={{ height: '100%' }} />
            )}
        </div>
    );
};
FJMobileImageExtenderCreation.propTypes = {
    active: PropTypes.bool,
    uploadImageInfo: PropTypes.object,
    boardSize: PropTypes.object,
    imageRect: PropTypes.object,
    setImageRect: PropTypes.func,
    curRatioName: PropTypes.string,
    taskStatus: PropTypes.string,
    creationList: PropTypes.array,
};

export default FJMobileImageExtenderCreation;
